import { FormControl, FormHelperText, MenuItem, MenuItemProps, Select, Divider } from '@mui/material'
import { SelectProps } from '@mui/material/Select/Select'
import { visuallyHidden } from '@mui/utils'
import { C1FormLabel } from 'components/core/RHKFormComponents/C1FormLabel'
import { C1LabeledSelectStyles } from 'components/core/RHKFormComponents/C1LabeledSelectStyles'
import { ReactNode } from 'react'
import { Control, Controller } from 'react-hook-form'
import { Message } from 'react-hook-form/dist/types/errors'

// ---- Custom Select ----
type SelectOption = {
    label: ReactNode
    id: string
} & Pick<MenuItemProps, 'value'>

export type C1LabeledSelectProps = {
    control: Control<any> // eslint-disable-line @typescript-eslint/no-explicit-any
    name: string
    options?: SelectOption[][] | SelectOption[]
    error: boolean
    helperText?: Message
    label: ReactNode
    hideLabel?: boolean
} & SelectProps

export const C1LabeledSelect = ({
    control,
    name,
    options,
    error,
    helperText,
    label,
    hideLabel,
    ...props
}: C1LabeledSelectProps): React.ReactElement => {
    const labelId = `label-${name}`

    const renderOptions = (optionsArray: SelectOption[][] | SelectOption[]): ReactNode[] => {
        if (Array.isArray(optionsArray[0])) {
            const flatOptions: ReactNode[] = []

            optionsArray.forEach((group, index) => {
                group.forEach((option) => {
                    flatOptions.push(
                        <MenuItem key={option.id} value={option.value}>
                            {option.label}
                        </MenuItem>,
                    )
                })
                if (index < optionsArray.length - 1) {
                    flatOptions.push(<Divider key={`divider-${index}`} />)
                }
            })

            return flatOptions
        }
        return optionsArray.map((option) => (
            <MenuItem key={option.id} value={option.value}>
                {option.label}
            </MenuItem>
        ))
    }

    return (
        <FormControl fullWidth={props.fullWidth} error={error}>
            <C1FormLabel label={label} htmlFor={name} style={hideLabel ? visuallyHidden : undefined} />
            <Controller
                name={name}
                control={control}
                render={({ field }): React.ReactElement => <Select
                    {...props}
                    id={name}
                    labelId={labelId}
                    onChange={(e): void => field.onChange(e.target.value)}
                    value={field.value}
                    onBlur={field.onBlur}
                    sx={C1LabeledSelectStyles.selectOverrides}
                    size="small"
                >
                    {options && renderOptions(options)}
                </Select>
                }
            />
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    )
}

export type C1LabeledSelectRenderProps = {
    name: string
    options?: SelectOption[][] | SelectOption[]
    error: boolean
    helperText?: ReactNode
    label: ReactNode
    hideLabel?: boolean
} & SelectProps

export const C1LabeledSelectRender = ({
    name,
    options,
    error,
    helperText,
    label,
    hideLabel,
    ...props
}: C1LabeledSelectRenderProps): React.ReactElement => {
    const labelId = `label-${name}`

    const renderOptions = (opts: SelectOption[][] | SelectOption[]): ReactNode[] => {
        if (Array.isArray(opts[0])) {
            const flat: ReactNode[] = []
            ;(opts as SelectOption[][]).forEach((group, idx) => {
                group.forEach((o) => flat.push(<MenuItem key={o.id} value={o.value}>{o.label}</MenuItem>))
                if (idx < (opts as SelectOption[][]).length - 1) flat.push(<Divider key={`divider-${idx}`} />)
            })
            return flat
        }
        return (opts as SelectOption[]).map((o) => (
            <MenuItem key={o.id} value={o.value}>{o.label}</MenuItem>
        ))
    }

    return (
        <FormControl disabled={props.disabled} fullWidth={props.fullWidth} error={error}>
            <C1FormLabel label={label} htmlFor={name} style={hideLabel ? visuallyHidden : undefined} />
            <Select
                {...props}
                name={name}
                id={name}
                labelId={labelId}
                sx={C1LabeledSelectStyles.selectOverrides}
                size="small"
                disabled={props.disabled}
            >
                {options && renderOptions(options)}
            </Select>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    )
}
